import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { ArrowLeft, Mic, Send, Loader2, Play, Pause, Square, Smartphone, Power, PowerOff, Save, Upload, FileText, Trash2, Copy, Globe, Code, Users, Brain, Sparkles, RefreshCw, Mail, Phone, MessageCircle, BotOff, Bot, Share2 } from "lucide-react";

const VOICES = ["alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer"];
const LANGUAGES = [
  { code: "auto", label: "Auto-detect (recommended)" },
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" }, { code: "fr", label: "French" },
  { code: "de", label: "German" }, { code: "hi", label: "Hindi" }, { code: "pt", label: "Portuguese" },
  { code: "it", label: "Italian" }, { code: "ja", label: "Japanese" },
];

export default function AgentDetail() {
  const { id } = useParams();
  const { user, loading, setUser } = useAuth({ redirectIfUnauthed: true });
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [tab, setTab] = useState("test");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [savingForm, setSavingForm] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (loading || !user) return;
    (async () => {
      try {
        const [a, m] = await Promise.all([
          api.get(`/agents/${id}`),
          api.get(`/agents/${id}/messages`),
        ]);
        setAgent(a.data);
        setMessages(m.data || []);
      } catch {
        toast.error("Agent not found");
        navigate("/dashboard");
      }
    })();
  }, [id, loading, user, navigate]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    navigate("/");
  };

  const sendText = async () => {
    if (!text.trim() || sending) return;
    const userText = text.trim();
    setText("");
    setSending(true);
    setMessages((m) => [...m, { id: `tmp_${Date.now()}`, role: "user", text: userText, created_at: new Date().toISOString() }]);
    try {
      const res = await api.post(`/agents/${id}/chat`, { text: userText });
      const assistant = res.data.assistant_message;
      setMessages((m) => {
        const filtered = m.filter((x) => !x.id.startsWith("tmp_"));
        return [...filtered, res.data.user_message, assistant];
      });
      // auto-play
      if (assistant.audio_b64) playAudio(assistant.id, assistant.audio_b64);
    } catch {
      toast.error("Reply failed");
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        stream.getTracks().forEach((t) => t.stop());
        await sendVoice(blob, mime);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const sendVoice = async (blob, mime) => {
    setSending(true);
    const tmpId = `tmp_${Date.now()}`;
    setMessages((m) => [...m, { id: tmpId, role: "user", text: "🎙️ Voice note…", created_at: new Date().toISOString() }]);
    const fd = new FormData();
    const ext = mime.includes("webm") ? "webm" : "m4a";
    fd.append("audio", blob, `recording.${ext}`);
    try {
      const res = await api.post(`/agents/${id}/voice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const assistant = res.data.assistant_message;
      setMessages((m) => {
        const filtered = m.filter((x) => x.id !== tmpId);
        return [...filtered, res.data.user_message, assistant];
      });
      if (assistant.audio_b64) playAudio(assistant.id, assistant.audio_b64);
    } catch {
      toast.error("Voice processing failed");
      setMessages((m) => m.filter((x) => x.id !== tmpId));
    } finally {
      setSending(false);
    }
  };

  const playAudio = (msgId, b64) => {
    if (!b64) return;
    if (audioRef.current) { audioRef.current.pause(); }
    const audio = new Audio(`data:audio/mp3;base64,${b64}`);
    audioRef.current = audio;
    setPlayingId(msgId);
    audio.onended = () => setPlayingId(null);
    audio.play().catch(() => setPlayingId(null));
  };

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingId(null);
  };

  const updateAgent = async (patch) => {
    setSavingForm(true);
    try {
      const res = await api.put(`/agents/${id}`, patch);
      setAgent(res.data);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingForm(false);
    }
  };

  if (loading || !agent) {
    return (
      <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] flex items-center justify-center font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F]">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain">
      <Navbar user={user} onLogout={handleLogout} />

      <div className="mx-auto max-w-7xl px-6 md:px-12 py-10">
        <Link to="/dashboard" className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] flex items-center gap-2 mb-6" data-testid="back-to-dashboard">
          <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back
        </Link>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F] mb-2">Agent</div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">{agent.name}</h1>
            <div className="font-mono text-xs text-[#A39A8F] mt-2 flex gap-3">
              <span>Voice: <span className="text-[#F5F2EB]">{agent.voice}</span></span>
              <span>·</span>
              <span>Lang: <span className="text-[#F5F2EB]">{agent.language}</span></span>
              <span>·</span>
              <span className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${agent.whatsapp_connected ? "bg-green-500 pulse-green" : "bg-[#A39A8F]"}`} />
                {agent.whatsapp_connected ? "WhatsApp live" : "Offline"}
              </span>
            </div>
          </div>
          <div className="flex gap-1 border border-[#F5F2EB]/15 self-start flex-wrap" data-testid="agent-tabs">
            {[
              { k: "test", l: "Test chat" },
              { k: "configure", l: "Configure" },
              { k: "knowledge", l: "Knowledge" },
              { k: "contacts", l: "Contacts" },
              { k: "share", l: "Share / Embed" },
              { k: "connect", l: "WhatsApp" },
              { k: "meta", l: "Meta channels" },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`tab-${t.k}`}
                className={`px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${tab === t.k ? "bg-[#D95B38] text-[#F5F2EB]" : "text-[#A39A8F] hover:text-[#F5F2EB]"}`}
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>

        {tab === "test" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 rounded-md overflow-hidden flex flex-col h-[600px] shadow-2xl" data-testid="chat-panel">
                <div className="bg-[#1A1715] p-4 border-b border-[#F5F2EB]/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D95B38]/20 flex items-center justify-center font-serif text-lg">{agent.name[0]?.toUpperCase()}</div>
                    <div>
                      <div className="font-mono text-sm">{agent.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block" />
                        online
                      </div>
                    </div>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">Simulated WhatsApp</div>
                </div>

                <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3 flex flex-col" data-testid="chat-messages">
                  {messages.length === 0 && (
                    <div className="m-auto text-center font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F]">
                      Send a message or record a voice note ↓
                    </div>
                  )}
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div
                        data-testid={`message-${m.role}`}
                        className={`max-w-[78%] p-3 font-mono text-sm shadow-sm ${
                          m.role === "user"
                            ? "bg-[#D95B38] text-[#F5F2EB] rounded-l-md rounded-tr-md"
                            : "bg-[#1A1715] border border-[#F5F2EB]/10 text-[#F5F2EB] rounded-r-md rounded-tl-md"
                        }`}
                      >
                        <div className="whitespace-pre-wrap">{m.text}</div>
                        {m.audio_b64 && (
                          <button
                            onClick={() => (playingId === m.id ? stopAudio() : playAudio(m.id, m.audio_b64))}
                            className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] opacity-80 hover:opacity-100"
                            data-testid={`play-audio-${m.id}`}
                          >
                            {playingId === m.id ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                            {playingId === m.id ? "Pause" : "Play voice"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="flex justify-start">
                      <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-3 rounded-r-md rounded-tl-md">
                        <span className="wave-bar h-4 text-[#D95B38]" style={{ animationDelay: "0s" }} />
                        <span className="wave-bar h-4 text-[#D95B38]" style={{ animationDelay: "0.15s" }} />
                        <span className="wave-bar h-4 text-[#D95B38]" style={{ animationDelay: "0.3s" }} />
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-[#1A1715] p-3 border-t border-[#F5F2EB]/10 flex gap-2 items-center">
                  {recording ? (
                    <button
                      onClick={stopRecording}
                      data-testid="stop-record"
                      className="w-10 h-10 bg-[#D95B38] flex items-center justify-center btn-press"
                    >
                      <Square className="w-4 h-4 fill-[#F5F2EB] text-[#F5F2EB]" />
                    </button>
                  ) : (
                    <button
                      onClick={startRecording}
                      disabled={sending}
                      data-testid="start-record"
                      className="w-10 h-10 border border-[#F5F2EB]/20 hover:border-[#D95B38] hover:text-[#D95B38] flex items-center justify-center transition-colors disabled:opacity-40"
                    >
                      <Mic className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                  <input
                    value={text}
                    disabled={sending || recording}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") sendText(); }}
                    placeholder={recording ? "Recording…" : "Type a message or hit the mic"}
                    data-testid="chat-input"
                    className="flex-1 bg-transparent border-b border-[#F5F2EB]/15 font-mono text-sm px-2 py-2 focus:outline-none focus:border-[#D95B38] disabled:opacity-50"
                  />
                  <button
                    onClick={sendText}
                    disabled={!text.trim() || sending}
                    data-testid="send-text"
                    className="w-10 h-10 bg-[#D95B38] text-[#F5F2EB] flex items-center justify-center btn-press disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>
            </div>

            <aside className="lg:col-span-4 space-y-6">
              <div className="border border-[#F5F2EB]/10 bg-[#1A1715] p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Try these</div>
                <ul className="space-y-3 font-mono text-sm">
                  {[
                    "What are your hours?",
                    "Can you book me a table?",
                    "Tell me a joke",
                  ].map((s) => (
                    <li key={s}>
                      <button
                        onClick={() => setText(s)}
                        className="text-left text-[#E0D8CA] hover:text-[#D95B38] transition-colors"
                        data-testid={`suggest-${s.slice(0, 8)}`}
                      >
                        → {s}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-[#F5F2EB]/10 bg-[#1A1715] p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Persona snippet</div>
                <p className="font-serif italic text-base text-[#F5F2EB] leading-relaxed">"{agent.persona.slice(0, 220)}{agent.persona.length > 220 ? "…" : ""}"</p>
              </div>
            </aside>
          </div>
        )}

        {tab === "configure" && (
          <div className="max-w-2xl bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10" data-testid="configure-panel">
            <ConfigureForm agent={agent} savingForm={savingForm} onSave={updateAgent} />
          </div>
        )}

        {tab === "knowledge" && (
          <KnowledgePanel agentId={agent.id} />
        )}

        {tab === "contacts" && (
          <ContactsPanel agentId={agent.id} />
        )}

        {tab === "share" && (
          <SharePanel agent={agent} onSave={updateAgent} savingForm={savingForm} />
        )}

        {tab === "connect" && (
          <ConnectTab agent={agent} updateAgent={updateAgent} savingForm={savingForm} />
        )}

        {tab === "meta" && (
          <MetaChannelsPanel agentId={agent.id} />
        )}
      </div>
    </div>
  );
}

function ConfigureForm({ agent, savingForm, onSave }) {
  const [form, setForm] = useState({
    name: agent.name, persona: agent.persona, voice: agent.voice, language: agent.language,
    knowledge: agent.knowledge || "",
    style_guide: agent.style_guide || "",
    text_mode: agent.text_mode || "auto",
    auto_reply_paused_message: agent.auto_reply_paused_message || "Thanks for your message — someone will get back to you shortly.",
    manual_contacts_text: (agent.manual_contacts || []).join(", "),
    intake_form: agent.intake_form || [],
    premium_voice_enabled: !!agent.premium_voice_enabled,
    premium_voice_id: agent.premium_voice_id || null,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const manual_contacts = form.manual_contacts_text
      .split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    const { manual_contacts_text, ...rest } = form;
    onSave({ ...rest, manual_contacts });
  };

  const updateIntakeField = (idx, patch) => {
    setForm((f) => ({
      ...f,
      intake_form: f.intake_form.map((row, i) => i === idx ? { ...row, ...patch } : row),
    }));
  };
  const removeIntakeField = (idx) => {
    setForm((f) => ({ ...f, intake_form: f.intake_form.filter((_, i) => i !== idx) }));
  };
  const addIntakeField = () => {
    setForm((f) => ({
      ...f,
      intake_form: [...f.intake_form, { key: "", question: "", confirm: false }],
    }));
  };
  const addPresetPhone = () => {
    setForm((f) => ({
      ...f,
      intake_form: [...f.intake_form, { key: "phone", question: "What's the best phone number to reach you on?", confirm: true }],
    }));
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      <Field label="Name">
        <input
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="cfg-name"
          className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-lg focus:outline-none focus:border-[#D95B38]"
        />
      </Field>
      <Field label="Persona">
        <textarea
          required rows={5}
          value={form.persona}
          onChange={(e) => setForm({ ...form, persona: e.target.value })}
          data-testid="cfg-persona"
          className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38] resize-none"
        />
      </Field>
      <Field label="Brand voice (style rules · max 500 chars)">
        <textarea
          rows={3}
          value={form.style_guide}
          onChange={(e) => setForm({ ...form, style_guide: e.target.value.slice(0, 500) })}
          data-testid="cfg-style-guide"
          placeholder="Warm. Short sentences. Use the customer's name once you have it. Never say 'no worries' or 'reach out'. Always end with a question."
          maxLength={500}
          className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38] resize-none"
        />
        <div className="font-mono text-[10px] text-[#A39A8F] mt-1 flex justify-between">
          <span>Pure style rules — tone, sentence length, banned phrases. Applied on top of persona.</span>
          <span data-testid="cfg-style-guide-count">{(form.style_guide || "").length} / 500</span>
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-6">
        <Field label="Voice (click to preview)">
          <VoicePicker
            voices={VOICES}
            selected={form.voice}
            onSelect={(v) => setForm({ ...form, voice: v })}
          />
        </Field>
        <Field label="Language">
          <select
            value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
            data-testid="cfg-language"
            className="w-full bg-[#1A1715] border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38]"
          >
            {LANGUAGES.map((l) => <option key={l.code} value={l.code} className="bg-[#1A1715]">{l.label}</option>)}
          </select>
        </Field>
      </div>
      <PremiumVoiceCard agentId={agent.id} form={form} setForm={setForm} onSave={onSave} />
      <Field label="WhatsApp reply mode">
        <select
          value={form.text_mode}
          onChange={(e) => setForm({ ...form, text_mode: e.target.value })}
          data-testid="cfg-text-mode"
          className="w-full bg-[#1A1715] border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38]"
        >
          <option value="auto" className="bg-[#1A1715]">Auto — mirror the customer (text → text, voice → voice)</option>
          <option value="always_text" className="bg-[#1A1715]">Always reply with text</option>
          <option value="always_voice" className="bg-[#1A1715]">Always reply with voice note</option>
          <option value="mirror" className="bg-[#1A1715]">Mirror exactly what was sent</option>
        </select>
      </Field>
      <Field label="Quick knowledge (inline)">
        <textarea
          rows={3}
          value={form.knowledge}
          onChange={(e) => setForm({ ...form, knowledge: e.target.value })}
          data-testid="cfg-knowledge"
          className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38] resize-none"
          placeholder="Short notes — for full PDFs/CSVs use the Knowledge tab"
        />
      </Field>
      <Field label="Message sent when AI auto-reply is paused">
        <textarea
          rows={2}
          value={form.auto_reply_paused_message}
          onChange={(e) => setForm({ ...form, auto_reply_paused_message: e.target.value })}
          data-testid="cfg-paused-msg"
          className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38] resize-none"
          placeholder="Thanks — someone will be in touch shortly."
        />
      </Field>
      <Field label="White List your important contacts like (wife or partner) (phone numbers or IDs, comma-separated)">
        <textarea
          rows={2}
          value={form.manual_contacts_text}
          onChange={(e) => setForm({ ...form, manual_contacts_text: e.target.value })}
          data-testid="cfg-manual-contacts"
          className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 px-0 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38] resize-none"
          placeholder="+14155551234, +447700900000"
        />
        <div className="font-mono text-[10px] text-[#A39A8F] mt-1">
          These contacts will always be created with the AI paused — you handle them manually.
        </div>
      </Field>

      <div data-testid="cfg-intake-builder" className="border-t border-[#F5F2EB]/10 pt-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-serif text-2xl">Intake form</div>
            <div className="font-mono text-[10px] text-[#A39A8F] uppercase tracking-[0.25em] mt-1">
              Questions the agent asks new contacts before chatting freely
            </div>
          </div>
          <div className="flex gap-2">
            {form.intake_form.length === 0 && (
              <button
                type="button"
                onClick={addPresetPhone}
                data-testid="intake-add-phone-preset"
                className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] hover:bg-[#D95B38] hover:text-[#F5F2EB] px-3 py-2 transition-colors"
              >
                + Phone (with read-back)
              </button>
            )}
            <button
              type="button"
              onClick={addIntakeField}
              data-testid="intake-add-field"
              className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] hover:bg-[#F5F2EB] hover:text-[#0F0D0C] px-3 py-2 transition-colors"
            >
              + Add field
            </button>
          </div>
        </div>

        {form.intake_form.length === 0 ? (
          <div className="border border-dashed border-[#F5F2EB]/15 p-6 font-mono text-xs text-[#A39A8F] text-center">
            No intake questions. Agent chats freely from message one.
          </div>
        ) : (
          <ul className="space-y-3">
            {form.intake_form.map((field, i) => {
              const earlierKeys = form.intake_form
                .slice(0, i)
                .map((r) => (r.key || "").trim())
                .filter((k) => k);
              const cond = field.show_if || null;
              const hasOptions = Array.isArray(field.options) && field.options.length > 0;
              return (
              <li key={i} data-testid={`intake-field-${i}`} className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                  <div className="md:col-span-3">
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-1">Key</label>
                    <input
                      value={field.key}
                      onChange={(e) => updateIntakeField(i, { key: e.target.value.replace(/\s/g, "_").toLowerCase() })}
                      placeholder="phone"
                      data-testid={`intake-key-${i}`}
                      maxLength={40}
                      className="w-full bg-transparent border-b border-[#F5F2EB]/20 font-mono text-sm py-1 focus:outline-none focus:border-[#D95B38]"
                    />
                  </div>
                  <div className="md:col-span-7">
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-1">Question</label>
                    <input
                      value={field.question}
                      onChange={(e) => updateIntakeField(i, { question: e.target.value })}
                      placeholder="What's the best phone number to reach you on?"
                      data-testid={`intake-question-${i}`}
                      maxLength={300}
                      className="w-full bg-transparent border-b border-[#F5F2EB]/20 font-mono text-sm py-1 focus:outline-none focus:border-[#D95B38]"
                    />
                  </div>
                  <div className="md:col-span-2 flex items-end gap-2 pb-1 justify-between">
                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!field.confirm}
                        onChange={(e) => updateIntakeField(i, { confirm: e.target.checked })}
                        data-testid={`intake-confirm-${i}`}
                        className="accent-[#D95B38]"
                      />
                      Read back
                    </label>
                    <button
                      type="button"
                      onClick={() => removeIntakeField(i)}
                      data-testid={`intake-remove-${i}`}
                      className="text-[#A39A8F] hover:text-[#D95B38] transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>

                {/* Options row */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                  <div className="md:col-span-12">
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-1">
                      Options <span className="text-[#A39A8F]/60 lowercase tracking-normal">(optional · comma-separated · max 8)</span>
                    </label>
                    <input
                      value={(field.options || []).join(", ")}
                      onChange={(e) => updateIntakeField(i, {
                        options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 8),
                      })}
                      placeholder="yes, no   ·   small, medium, large   ·   restaurant, salon, clinic"
                      data-testid={`intake-options-${i}`}
                      className="w-full bg-transparent border-b border-[#F5F2EB]/20 font-mono text-sm py-1 focus:outline-none focus:border-[#D95B38]"
                    />
                  </div>
                </div>

                {/* Branching row — only meaningful if there are earlier fields to reference */}
                {earlierKeys.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-5">
                      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-1">Only ask if (field)</label>
                      <select
                        value={cond?.field || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          updateIntakeField(i, { show_if: v ? { field: v, equals: cond?.equals || "" } : null });
                        }}
                        data-testid={`intake-showif-field-${i}`}
                        className="w-full bg-[#1A1715] border-b border-[#F5F2EB]/20 font-mono text-sm py-1 focus:outline-none focus:border-[#D95B38]"
                      >
                        <option value="" className="bg-[#1A1715]">— Always ask —</option>
                        {earlierKeys.map((k) => (
                          <option key={k} value={k} className="bg-[#1A1715]">{k}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-7">
                      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-1">…equals</label>
                      <input
                        value={cond?.equals && !Array.isArray(cond.equals) ? cond.equals : (Array.isArray(cond?.equals) ? cond.equals.join(", ") : "")}
                        onChange={(e) => {
                          if (!cond?.field) return;
                          const raw = e.target.value;
                          const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
                          const eq = parts.length > 1 ? parts.slice(0, 8) : raw;
                          updateIntakeField(i, { show_if: { field: cond.field, equals: eq } });
                        }}
                        disabled={!cond?.field}
                        placeholder={cond?.field ? "yes   ·   pro, lifetime" : "Pick a field first →"}
                        data-testid={`intake-showif-equals-${i}`}
                        className="w-full bg-transparent border-b border-[#F5F2EB]/20 font-mono text-sm py-1 focus:outline-none focus:border-[#D95B38] disabled:opacity-40"
                      />
                    </div>
                  </div>
                )}

                {/* Summary pills */}
                {(hasOptions || cond?.field) && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {hasOptions && (
                      <span data-testid={`intake-pill-options-${i}`} className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#D95B38]/40 text-[#D95B38] px-2 py-1">
                        {field.options.length} option{field.options.length === 1 ? "" : "s"}
                      </span>
                    )}
                    {cond?.field && (
                      <span data-testid={`intake-pill-branch-${i}`} className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#A39A8F] px-2 py-1">
                        Branch: {cond.field} = {Array.isArray(cond.equals) ? cond.equals.join("/") : (cond.equals || "(any)")}
                      </span>
                    )}
                  </div>
                )}
              </li>
            );})}
          </ul>
        )}
      </div>
      <button
        type="submit"
        disabled={savingForm}
        data-testid="cfg-save"
        className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-8 py-3 btn-press flex items-center gap-3 disabled:opacity-60"
      >
        {savingForm ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
        Save changes
      </button>
    </form>
  );
}

function KnowledgePanel({ agentId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/agents/${agentId}/knowledge`);
      setFiles(res.data || []);
    } catch { /* empty */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [agentId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/agents/${agentId}/knowledge`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} indexed`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (fid) => {
    if (!window.confirm("Remove this file from the knowledge base?")) return;
    try {
      await api.delete(`/agents/${agentId}/knowledge/${fid}`);
      setFiles((f) => f.filter((x) => x.id !== fid));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="knowledge-panel">
      <div className="lg:col-span-1 bg-[#1A1715] border border-[#F5F2EB]/10 p-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Upload</div>
        <h2 className="font-serif text-2xl mb-4">Teach your agent.</h2>
        <p className="font-mono text-sm text-[#A39A8F] mb-6 leading-relaxed">
          PDF, CSV, TXT, or MD. We chunk and index automatically. Up to 5 MB per file.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.csv,.txt,.md"
          onChange={handleUpload}
          className="hidden"
          data-testid="kb-file-input"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="kb-upload-btn"
          className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-3 btn-press flex items-center gap-3 disabled:opacity-60"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.5} />}
          {uploading ? "Indexing…" : "Choose file"}
        </button>
      </div>

      <div className="lg:col-span-2 bg-[#1A1715] border border-[#F5F2EB]/10 p-8">
        <div className="flex justify-between items-center mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F]">Library</div>
          <div className="font-mono text-[10px] text-[#A39A8F]">{files.length} file{files.length !== 1 ? "s" : ""}</div>
        </div>
        {loading ? (
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F]">Loading…</div>
        ) : files.length === 0 ? (
          <div className="border border-dashed border-[#F5F2EB]/15 p-10 text-center" data-testid="kb-empty">
            <FileText className="w-8 h-8 text-[#A39A8F] mx-auto mb-3" strokeWidth={1.2} />
            <div className="font-mono text-sm text-[#A39A8F]">No knowledge files yet.</div>
          </div>
        ) : (
          <ul className="divide-y divide-[#F5F2EB]/10">
            {files.map((f) => (
              <li key={f.id} data-testid={`kb-file-${f.id}`} className="py-4 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <FileText className="w-5 h-5 text-[#D95B38] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div className="font-serif text-lg truncate">{f.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">
                      {f.chunk_count} chunks · {(f.size_bytes / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(f.id)}
                  className="text-[#A39A8F] hover:text-[#D95B38] transition-colors"
                  data-testid={`kb-delete-${f.id}`}
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SharePanel({ agent, onSave, savingForm }) {
  const origin = window.location.origin;
  const chatUrl = `${origin}/chat/${agent.public_token}`;
  const shareUrl = `${origin}/api/share/${agent.public_token}`;
  const ogImageUrl = `${origin}/api/og/${agent.public_token}/image.png`;
  const embedSnippet = `<script src="${origin}/widget.js" data-token="${agent.public_token}"></script>`;
  const defaultTheme = {
    primary_color: "#D95B38", position: "bottom-right",
    header_text: "", welcome_message: "", launcher_label: "", brand_name: "", logo_b64: "",
  };
  const [theme, setTheme] = useState({ ...defaultTheme, ...(agent.widget_theme || {}) });
  const [logoError, setLogoError] = useState("");
  const [ogCacheBust, setOgCacheBust] = useState(0);
  const [referrals, setReferrals] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [savingBranding, setSavingBranding] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    api.get("/billing/referrals").then((r) => setReferrals(r.data)).catch(() => {});
    api.get("/billing/milestones").then((r) => setMilestones(r.data)).catch(() => {});
  }, []);

  const toggleBranding = async (next) => {
    if (savingBranding) return;
    setSavingBranding(true);
    try {
      await api.put("/billing/branding", { keep_branding: !!next });
      const r = await api.get("/billing/referrals");
      setReferrals(r.data);
      toast.success(next ? "Badge enabled — rewards active." : "Badge disabled.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally { setSavingBranding(false); }
  };

  const [claimingCoupon, setClaimingCoupon] = useState(false);
  const claimCoupon = async () => {
    if (claimingCoupon) return;
    setClaimingCoupon(true);
    try {
      const r = await api.post("/billing/rewards/claim-coupon");
      const code = r.data.promo_code;
      const fresh = await api.get("/billing/referrals");
      setReferrals(fresh.data);
      toast.success(r.data.already_claimed
        ? `Code already claimed: ${code}`
        : `Coupon ready! Use ${code} at checkout for 10% off.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not claim coupon");
    } finally { setClaimingCoupon(false); }
  };

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };
  const saveTheme = async () => {
    await onSave({ widget_theme: theme });
    setOgCacheBust(Date.now());
  };

  const onPickLogo = (e) => {
    setLogoError("");
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { setLogoError("Pick an image file (PNG, JPG, or SVG)."); return; }
    if (file.size > 250 * 1024) { setLogoError("Image too large — keep it under 250 KB."); return; }
    const reader = new FileReader();
    reader.onload = () => setTheme((t) => ({ ...t, logo_b64: reader.result }));
    reader.onerror = () => setLogoError("Could not read that file.");
    reader.readAsDataURL(file);
  };
  const clearLogo = () => {
    setTheme((t) => ({ ...t, logo_b64: "" }));
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  return (
    <div className="space-y-6" data-testid="share-panel">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Public chat page</div>
          <h2 className="font-serif text-2xl mb-3 flex items-center gap-3"><Globe className="w-6 h-6 text-[#D95B38]" strokeWidth={1.5} />Share a link</h2>
          <p className="font-mono text-sm text-[#A39A8F] mb-6 leading-relaxed">
            A standalone web page anyone can chat with. Great for email signatures, QR codes, link-in-bio.
          </p>
          <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-4 font-mono text-xs text-[#E0D8CA] break-all mb-4" data-testid="share-chat-url">
            {chatUrl}
          </div>
          <div className="flex gap-3">
            <button onClick={() => copy(chatUrl, "Link")} data-testid="copy-chat-url"
              className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-5 py-2.5 btn-press flex items-center gap-2">
              <Copy className="w-4 h-4" strokeWidth={1.5} /> Copy link
            </button>
            <a href={chatUrl} target="_blank" rel="noopener noreferrer" data-testid="open-chat-url"
              className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] px-5 py-2.5 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors flex items-center gap-2">
              Open ↗
            </a>
          </div>
        </div>

        <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Embed on any site</div>
          <h2 className="font-serif text-2xl mb-3 flex items-center gap-3"><Code className="w-6 h-6 text-[#D95B38]" strokeWidth={1.5} />Drop-in widget</h2>
          <p className="font-mono text-sm text-[#A39A8F] mb-6 leading-relaxed">
            Paste this snippet anywhere in your site — usually just before <code className="text-[#D95B38]">&lt;/body&gt;</code>.
          </p>
          <pre className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-4 font-mono text-xs text-[#E0D8CA] overflow-x-auto mb-4" data-testid="share-embed-snippet">
{embedSnippet}
          </pre>
          <button onClick={() => copy(embedSnippet, "Embed code")} data-testid="copy-embed-snippet"
            className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-5 py-2.5 btn-press flex items-center gap-2">
            <Copy className="w-4 h-4" strokeWidth={1.5} /> Copy snippet
          </button>
        </div>
      </div>

      {/* Social share preview — OG card */}
      <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10" data-testid="social-share-panel">
        <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-2">For LinkedIn, X, WhatsApp, iMessage, Slack…</div>
            <h2 className="font-serif text-2xl flex items-center gap-3"><Share2 className="w-6 h-6 text-[#D95B38]" strokeWidth={1.5} />Social share link</h2>
            <p className="font-mono text-xs text-[#A39A8F] mt-2 max-w-2xl leading-relaxed">
              Paste this anywhere a rich preview matters. Crawlers see a branded card; humans get bounced to the live chat in under a second.
            </p>
          </div>
          <button
            onClick={() => { setOgCacheBust(Date.now()); toast.success("Preview refreshed"); }}
            data-testid="og-refresh-preview"
            className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] px-4 py-2 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-start">
          {/* Live OG preview */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Live preview</div>
            <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-3 inline-block w-full">
              <img
                key={ogCacheBust}
                src={`${ogImageUrl}?v=${ogCacheBust}`}
                alt="Social share card preview"
                data-testid="og-preview-image"
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            <p className="font-mono text-[10px] text-[#7A7068] mt-2">
              1200 × 630 px · Updates within a few minutes of theme changes (or hit Refresh).
            </p>
          </div>

          {/* Share URL + actions */}
          <div className="space-y-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-2">Share URL</div>
              <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-3 font-mono text-xs text-[#E0D8CA] break-all" data-testid="social-share-url">
                {shareUrl}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => copy(shareUrl, "Share link")} data-testid="copy-social-share-url"
                className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-4 py-2.5 btn-press flex items-center gap-2">
                <Copy className="w-4 h-4" strokeWidth={1.5} /> Copy
              </button>
              <a href={shareUrl} target="_blank" rel="noopener noreferrer" data-testid="open-social-share-url"
                className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] px-4 py-2.5 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors flex items-center gap-2">
                Open ↗
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank" rel="noopener noreferrer" data-testid="share-on-linkedin"
                className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] px-4 py-2.5 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors"
              >
                LinkedIn
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent("Chat live with " + (agent.name || "my AI agent"))}`}
                target="_blank" rel="noopener noreferrer" data-testid="share-on-x"
                className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] px-4 py-2.5 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors"
              >
                X / Twitter
              </a>
            </div>
            <div className="border-t border-[#F5F2EB]/10 pt-4 mt-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-2">Tip</div>
              <p className="font-mono text-xs text-[#A39A8F] leading-relaxed">
                Change the brand colour, logo, or welcome message in <span className="text-[#E0D8CA]">Customise your widget</span> below — the share card updates automatically.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Branding rewards — viral loop */}
      {referrals && (
        <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10" data-testid="branding-rewards-panel">
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-2">Get paid to spread the word</div>
              <h2 className="font-serif text-2xl flex items-center gap-3">
                <Share2 className="w-6 h-6 text-[#D95B38]" strokeWidth={1.5} />
                "Powered by AgentReply" badge
              </h2>
              <p className="font-mono text-xs text-[#A39A8F] mt-2 max-w-2xl leading-relaxed">
                A tiny pill anchored next to your chat launcher. {referrals.is_trial
                  ? "On for trial accounts — upgrade to a paid plan to make it optional."
                  : "Keep it on and you'll earn rewards every month."}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${referrals.badge_active ? "text-[#D95B38]" : "text-[#7A7068]"}`}>
                {referrals.badge_active ? "● Active" : "○ Hidden"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-5">
                <div className="font-serif text-4xl text-[#D95B38] mb-1" data-testid="referral-signups-count">
                  {referrals.total_signups_attributed}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">
                  Signups from your widget
                </div>
              </div>
              <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-5">
                <div className="font-serif text-4xl text-[#D95B38] mb-1" data-testid="referral-converted-count">
                  {referrals.converted_to_paid}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">
                  Converted to paid
                </div>
              </div>
            </div>

            {/* Toggle + reward info */}
            <div className="space-y-4">
              {referrals.is_trial ? (
                <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2">Trial accounts</div>
                  <p className="font-mono text-xs text-[#E0D8CA] leading-relaxed">
                    The badge stays on while you're on the free trial. Upgrade to make it optional — and unlock rewards for keeping it on.
                  </p>
                </div>
              ) : (
                <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-5">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">Show badge</div>
                    <button
                      type="button"
                      onClick={() => toggleBranding(!referrals.keep_branding)}
                      disabled={savingBranding}
                      data-testid="toggle-branding-badge"
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${referrals.keep_branding ? "bg-[#D95B38]" : "bg-[#2B2624]"} disabled:opacity-60`}
                      aria-pressed={!!referrals.keep_branding}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-[#F5F2EB] transition-transform ${referrals.keep_branding ? "translate-x-8" : "translate-x-1"}`}
                      />
                    </button>
                  </div>
                  <p className="font-mono text-[11px] text-[#A39A8F] leading-relaxed">
                    {referrals.keep_branding
                      ? "Badge is ON. You're eligible for monthly rewards (claim by email)."
                      : "Badge is OFF. Turn it on to start earning."}
                  </p>
                </div>
              )}

              <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] mb-2">Rewards</div>
                <p className="font-mono text-xs text-[#E0D8CA] leading-relaxed mb-3">
                  Keep the badge on for 14 days → claim a 10%-off coupon (auto-applies at checkout). Plus $5 credit per converted paid signup.
                </p>
                {referrals.already_claimed_this_month && referrals.claimed_code ? (
                  <div className="bg-[#1A1715] border border-[#D95B38]/40 p-3 mb-3" data-testid="claimed-coupon-display">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-1">Your coupon (valid 30 days)</div>
                    <div className="font-mono text-sm text-[#F5F2EB] tracking-wider mb-2 select-all break-all">{referrals.claimed_code}</div>
                    <div className="flex gap-2 flex-wrap">
                      <a
                        href={`/?promo=${encodeURIComponent(referrals.claimed_code)}#pricing`}
                        data-testid="apply-coupon-at-checkout"
                        className="font-mono text-[10px] uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-3 py-2 hover:opacity-90 transition-opacity btn-press"
                      >
                        Apply at checkout →
                      </a>
                      <button
                        onClick={() => { navigator.clipboard.writeText(referrals.claimed_code); toast.success("Code copied"); }}
                        className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#F5F2EB]/20 text-[#A39A8F] px-3 py-2 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                ) : referrals.reward_eligible_this_month ? (
                  <button
                    onClick={claimCoupon}
                    disabled={claimingCoupon}
                    data-testid="claim-coupon-button"
                    className="w-full font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-4 py-3 hover:opacity-90 transition-opacity btn-press disabled:opacity-60 mb-3"
                  >
                    {claimingCoupon ? "Claiming…" : "Claim 10% off →"}
                  </button>
                ) : !referrals.is_trial && referrals.keep_branding ? (
                  <p className="font-mono text-[10px] text-[#A39A8F] mb-3 italic">
                    Coupon unlocks after the badge has been on for 14 days.
                  </p>
                ) : null}
                <Link to="/leaderboard" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] hover:underline inline-flex items-center gap-1" data-testid="rewards-leaderboard-link">
                  See the leaderboard →
                </Link>
                <p className="font-mono text-[10px] text-[#7A7068] mt-3 leading-relaxed">
                  Or email <a href="mailto:support@agentreply.io" className="text-[#D95B38] underline">support@agentreply.io</a> for the $5-per-signup credits.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Milestone ladder — every affiliate has a goal */}
      {milestones && (
        <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10" data-testid="milestone-ladder-panel">
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-2">Milestones</div>
              <h2 className="font-serif text-2xl">Your reward ladder</h2>
              <p className="font-mono text-xs text-[#A39A8F] mt-2 max-w-2xl leading-relaxed">
                Every paid signup attributed to your widget climbs you up the ladder. Hit a milestone, unlock a code. Codes are single-use and locked to your account.
              </p>
            </div>
            <div className="font-serif text-4xl text-[#D95B38]" data-testid="milestone-converted-count">
              {milestones.converted_to_paid}
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] ml-2">converted</span>
            </div>
          </div>

          {/* Next milestone progress bar */}
          {milestones.next_milestone ? (
            <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-5 mb-6" data-testid="next-milestone-card">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38]">Next: {milestones.next_milestone.label}</div>
                <div className="font-mono text-xs text-[#A39A8F]">
                  {milestones.next_milestone.progress} / {milestones.next_milestone.threshold}
                </div>
              </div>
              <div className="font-serif text-lg text-[#F5F2EB] mb-3">{milestones.next_milestone.headline}</div>
              <div className="h-2 bg-[#2B2624] overflow-hidden">
                <div
                  className="h-full bg-[#D95B38] transition-all"
                  style={{ width: `${Math.min(100, (milestones.next_milestone.progress / milestones.next_milestone.threshold) * 100)}%` }}
                  data-testid="milestone-progress-bar"
                />
              </div>
              <div className="font-mono text-[10px] text-[#7A7068] mt-2">
                {milestones.next_milestone.remaining} more paid signup{milestones.next_milestone.remaining === 1 ? "" : "s"} to unlock
              </div>
            </div>
          ) : (
            <div className="bg-[#1F1814] border border-[#F4C75A]/40 p-5 mb-6" data-testid="milestones-complete-card">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F4C75A]">🏆 Legend status</span>
              </div>
              <p className="font-mono text-sm text-[#F5F2EB]">
                You've unlocked every milestone. Time to chase the monthly leaderboard top 2.
              </p>
            </div>
          )}

          {/* Active codes */}
          {milestones.active_codes?.length > 0 && (
            <div className="space-y-2 mb-6" data-testid="milestone-active-codes">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2">Unredeemed reward codes</div>
              {milestones.active_codes.map((c) => (
                <div key={c.promo_code} className="bg-[#0F0D0C] border border-[#D95B38]/30 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`milestone-code-${c.promo_code}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-[#F5F2EB] tracking-wider break-all">{c.promo_code}</div>
                    <div className="font-mono text-[10px] text-[#A39A8F]">
                      {c.label} · {c.discount_percent}% off
                      {c.plan_restrict?.length > 0 ? ` · ${c.plan_restrict.join("/")} plan` : ""}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <a
                      href={`/?promo=${encodeURIComponent(c.promo_code)}#pricing`}
                      className="font-mono text-[10px] uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-3 py-2 hover:opacity-90 transition-opacity btn-press"
                      data-testid={`apply-milestone-${c.promo_code}`}
                    >
                      Apply →
                    </a>
                    <button
                      onClick={() => { navigator.clipboard.writeText(c.promo_code); toast.success("Code copied"); }}
                      className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#F5F2EB]/20 text-[#A39A8F] px-3 py-2 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* The ladder preview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {milestones.ladder.map((m) => {
              const unlocked = milestones.granted_milestones.includes(m.id);
              return (
                <div key={m.id}
                  className={`p-4 border ${unlocked ? "bg-[#1F1814] border-[#F4C75A]/40" : "bg-[#0F0D0C] border-[#F5F2EB]/10"}`}
                  data-testid={`milestone-tile-${m.id}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] mb-1">{m.threshold} signup{m.threshold === 1 ? "" : "s"}</div>
                  <div className="font-serif text-sm text-[#F5F2EB] leading-snug">{m.headline}</div>
                  {unlocked && <div className="font-mono text-[10px] text-[#F4C75A] mt-2">✓ Unlocked</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Theme customiser */}
      <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10" data-testid="widget-customiser">
        <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-2">Look & feel</div>
            <h2 className="font-serif text-2xl">Customise your widget.</h2>
            <p className="font-mono text-xs text-[#A39A8F] mt-2 max-w-xl">Match your brand. Changes apply to both the public page and the embed widget. Saves to all visitors instantly.</p>
          </div>
          <button onClick={saveTheme} disabled={savingForm} data-testid="widget-save-theme"
            className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-3 btn-press flex items-center gap-2 disabled:opacity-60">
            {savingForm ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
            Save theme
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Brand colour">
                <div className="flex items-center gap-3">
                  <input type="color" value={theme.primary_color}
                    onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })}
                    data-testid="theme-color-picker"
                    className="w-12 h-10 cursor-pointer bg-transparent border border-[#F5F2EB]/20"
                  />
                  <input type="text" value={theme.primary_color}
                    onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })}
                    data-testid="theme-color-hex"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="flex-1 bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2 focus:outline-none focus:border-[#D95B38]"
                  />
                </div>
              </Field>
              <Field label="Launcher position">
                <div className="flex gap-1 border border-[#F5F2EB]/15">
                  {[
                    { k: "bottom-right", l: "Bottom right" },
                    { k: "bottom-left", l: "Bottom left" },
                  ].map((p) => (
                    <button key={p.k} type="button"
                      onClick={() => setTheme({ ...theme, position: p.k })}
                      data-testid={`theme-pos-${p.k}`}
                      className={`flex-1 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${theme.position === p.k ? "bg-[#D95B38] text-[#F5F2EB]" : "text-[#A39A8F] hover:text-[#F5F2EB]"}`}>
                      {p.l}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <Field label="Header text (default: agent name)">
              <input type="text" value={theme.header_text}
                onChange={(e) => setTheme({ ...theme, header_text: e.target.value })}
                placeholder={agent.name}
                data-testid="theme-header-text"
                maxLength={60}
                className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2 focus:outline-none focus:border-[#D95B38]"
              />
            </Field>
            <Field label="Welcome message (shown when chat first opens)">
              <textarea rows={2} value={theme.welcome_message}
                onChange={(e) => setTheme({ ...theme, welcome_message: e.target.value })}
                placeholder="Hi! I'm here to help. What can I do for you today?"
                data-testid="theme-welcome"
                maxLength={200}
                className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2 focus:outline-none focus:border-[#D95B38] resize-none"
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Launcher button label (optional)">
                <input type="text" value={theme.launcher_label}
                  onChange={(e) => setTheme({ ...theme, launcher_label: e.target.value })}
                  placeholder="Chat with us"
                  data-testid="theme-launcher-label"
                  maxLength={30}
                  className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2 focus:outline-none focus:border-[#D95B38]"
                />
              </Field>
              <Field label="Brand name in footer (default: AgentReply)">
                <input type="text" value={theme.brand_name}
                  onChange={(e) => setTheme({ ...theme, brand_name: e.target.value })}
                  placeholder="AgentReply"
                  data-testid="theme-brand-name"
                  maxLength={40}
                  className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2 focus:outline-none focus:border-[#D95B38]"
                />
              </Field>
            </div>
            <Field label="Logo / avatar (PNG, JPG, SVG · max 250KB · shown in chat header & launcher)">
              <div className="flex items-center gap-4 flex-wrap">
                {theme.logo_b64 ? (
                  <img src={theme.logo_b64} alt="" data-testid="theme-logo-preview"
                    className="w-14 h-14 object-cover rounded-sm border border-[#F5F2EB]/15" />
                ) : (
                  <div className="w-14 h-14 border border-dashed border-[#F5F2EB]/20 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8F]">
                    no logo
                  </div>
                )}
                <input ref={logoInputRef} type="file" accept="image/*" onChange={onPickLogo}
                  data-testid="theme-logo-input"
                  className="hidden" />
                <button type="button" onClick={() => logoInputRef.current?.click()} data-testid="theme-logo-upload"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] px-4 py-2 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors flex items-center gap-2">
                  <Upload className="w-3.5 h-3.5" strokeWidth={1.8} /> {theme.logo_b64 ? "Replace" : "Upload"}
                </button>
                {theme.logo_b64 && (
                  <button type="button" onClick={clearLogo} data-testid="theme-logo-clear"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] px-2">
                    Remove
                  </button>
                )}
                {logoError && (
                  <div data-testid="theme-logo-error" className="font-mono text-xs text-[#D95B38] basis-full">{logoError}</div>
                )}
              </div>
            </Field>
          </div>

          {/* Live preview */}
          <div className="lg:col-span-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Live preview</div>
            <div className="relative bg-[#0F0D0C] border border-[#F5F2EB]/10 h-[340px] overflow-hidden" data-testid="theme-preview">
              <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle, #4A3E38 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
              {/* Floating launcher */}
              <div className="absolute bottom-4 flex items-center"
                style={{ [theme.position === "bottom-left" ? "left" : "right"]: "16px" }}>
                <button
                  style={{ background: theme.primary_color }}
                  className={`flex items-center gap-2 text-[#F5F2EB] shadow-lg ${theme.launcher_label ? "px-4 py-3 rounded-full" : "w-12 h-12 rounded-full"}`}
                >
                  {theme.logo_b64 ? (
                    <img src={theme.logo_b64} alt="" className="w-7 h-7 object-cover rounded-full bg-white" />
                  ) : (
                    <MessageCircle className="w-5 h-5" strokeWidth={1.8} />
                  )}
                  {theme.launcher_label && <span className="font-sans text-xs">{theme.launcher_label}</span>}
                </button>
              </div>
              {/* Mock chat header */}
              <div className="absolute top-0 left-0 right-0 bg-[#1A1715] border-b border-[#F5F2EB]/10 p-3 flex items-center gap-2">
                {theme.logo_b64 ? (
                  <img src={theme.logo_b64} alt="" className="w-7 h-7 object-cover rounded-sm" />
                ) : (
                  <div style={{ background: theme.primary_color + "33", color: theme.primary_color }} className="w-7 h-7 flex items-center justify-center font-serif">
                    {(theme.header_text || agent.name)[0]?.toUpperCase()}
                  </div>
                )}
                <div className="font-mono text-xs truncate" data-testid="preview-header">{theme.header_text || agent.name}</div>
              </div>
              {theme.welcome_message && (
                <div className="absolute top-14 left-3 right-12 bg-[#1A1715] border border-[#F5F2EB]/10 p-2 text-xs text-[#E0D8CA] font-mono rounded-r-md rounded-tl-md">
                  {theme.welcome_message}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <UsageSparkline agentId={agent.id} accent={theme.primary_color} />
    </div>
  );
}

function UsageSparkline({ agentId, accent }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/agents/${agentId}/usage-sparkline?days=14`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError("Could not load usage stats.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [agentId]);

  const renderBars = (series, label, color) => {
    const max = Math.max(1, ...series);
    return (
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2">{label}</div>
        <div className="flex items-end gap-[3px] h-16" data-testid={`sparkline-${label.toLowerCase().replace(/\s+/g, "-")}`}>
          {series.map((v, i) => (
            <div key={i} className="flex-1 relative group">
              <div
                style={{ height: `${(v / max) * 100}%`, background: color, opacity: v === 0 ? 0.15 : 1 }}
                className="w-full transition-all"
                title={`${data.labels[i]}: ${v}`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10" data-testid="usage-sparkline-panel">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-2">Last 14 days</div>
          <h2 className="font-serif text-2xl">Usage at a glance.</h2>
          <p className="font-mono text-xs text-[#A39A8F] mt-2 max-w-xl">Visitor messages, unique contacts, and rate-limit blocks across all public + embed chats.</p>
        </div>
        {data && (
          <div className="flex gap-6 font-mono text-xs">
            <Stat label="Messages" value={data.totals.messages} />
            <Stat label="Contacts" value={data.totals.unique_contacts} />
            <Stat label="Blocked" value={data.totals.rate_limit_blocks} accent={data.totals.rate_limit_blocks > 0 ? "#D95B38" : undefined} />
          </div>
        )}
      </div>
      {loading && <div className="font-mono text-xs text-[#A39A8F]" data-testid="sparkline-loading">Loading…</div>}
      {error && <div className="font-mono text-xs text-[#D95B38]" data-testid="sparkline-error">{error}</div>}
      {data && (
        <div className="flex flex-col md:flex-row gap-8">
          {renderBars(data.messages, "Messages", accent || "#D95B38")}
          {renderBars(data.unique_contacts, "Unique contacts", "#E0D8CA")}
          {renderBars(data.rate_limit_blocks, "Rate-limit blocks", "#8F2C24")}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="font-serif text-2xl" style={accent ? { color: accent } : undefined}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mt-1">{label}</div>
    </div>
  );
}

function ContactsPanel({ agentId }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all"); // all | followup | resolved | paused

  const loadContacts = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/agents/${agentId}/contacts`);
      setContacts(res.data || []);
      if ((res.data || []).length && !selectedId) {
        setSelectedId(res.data[0].contact_id);
      }
    } catch { /* empty */ }
    setLoading(false);
  };

  useEffect(() => { loadContacts(); /* eslint-disable-next-line */ }, [agentId]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    (async () => {
      try {
        const res = await api.get(`/agents/${agentId}/contacts/${selectedId}`);
        setDetail(res.data);
      } catch { setDetail(null); }
    })();
  }, [selectedId, agentId]);

  const updateContact = async (patch) => {
    if (!selectedId) return;
    try {
      const res = await api.put(`/agents/${agentId}/contacts/${selectedId}`, patch);
      setDetail((d) => d ? { ...d, contact: res.data } : d);
      setContacts((cs) => cs.map((c) => c.contact_id === selectedId ? { ...c, ...res.data } : c));
      toast.success("Saved");
    } catch {
      toast.error("Update failed");
    }
  };

  const refreshMemory = async () => {
    if (!selectedId) return;
    setRefreshing(true);
    try {
      await api.post(`/agents/${agentId}/contacts/${selectedId}/refresh-memory`);
      toast.success("Memory refreshing — give it a few seconds, then reload.");
      // Re-fetch after a delay
      setTimeout(async () => {
        try {
          const res = await api.get(`/agents/${agentId}/contacts/${selectedId}`);
          setDetail(res.data);
        } catch { /* empty */ }
        setRefreshing(false);
      }, 4000);
    } catch {
      toast.error("Refresh failed");
      setRefreshing(false);
    }
  };

  const forgetContact = async () => {
    if (!selectedId) return;
    if (!window.confirm("Forget this person? Their messages and memory will be erased.")) return;
    try {
      await api.delete(`/agents/${agentId}/contacts/${selectedId}`);
      toast.success("Contact forgotten");
      setSelectedId(null);
      setDetail(null);
      await loadContacts();
    } catch {
      toast.error("Delete failed");
    }
  };

  const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };

  if (loading) {
    return <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F]">Loading contacts…</div>;
  }

  const filteredContacts = contacts.filter((c) => {
    if (filter === "all") return true;
    if (filter === "paused") return c.ai_paused;
    return (c.status || "open") === filter;
  });

  const waToHref = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");
    return digits ? `https://wa.me/${digits}` : null;
  };

  const statusBadgeCls = (s) => {
    if (s === "followup") return "border-[#D95B38] text-[#D95B38]";
    if (s === "resolved") return "border-green-500/60 text-green-500";
    return "border-[#F5F2EB]/20 text-[#A39A8F]";
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="contacts-panel">
      <div className="lg:col-span-4 bg-[#1A1715] border border-[#F5F2EB]/10 p-6 max-h-[700px] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F]">People</div>
          <div className="font-mono text-[10px] text-[#A39A8F]">{filteredContacts.length}/{contacts.length}</div>
        </div>

        <div className="flex gap-1 mb-4 border border-[#F5F2EB]/10" data-testid="contacts-filter">
          {[
            { k: "all", l: "All" },
            { k: "followup", l: "Follow-up" },
            { k: "open", l: "Open" },
            { k: "resolved", l: "Resolved" },
            { k: "paused", l: "AI paused" },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`flex-1 px-2 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] transition-colors ${filter === f.k ? "bg-[#D95B38] text-[#F5F2EB]" : "text-[#A39A8F] hover:text-[#F5F2EB]"}`}
            >
              {f.l}
            </button>
          ))}
        </div>

        {contacts.length === 0 ? (
          <div className="border border-dashed border-[#F5F2EB]/15 p-8 text-center" data-testid="contacts-empty">
            <Users className="w-7 h-7 text-[#A39A8F] mx-auto mb-3" strokeWidth={1.2} />
            <div className="font-mono text-sm text-[#A39A8F] mb-2">No one's chatted yet.</div>
            <div className="font-mono text-[10px] text-[#A39A8F]">Share your agent's link or embed widget to start.</div>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F] py-6 text-center">No contacts match this filter.</div>
        ) : (
          <ul className="divide-y divide-[#F5F2EB]/10">
            {filteredContacts.map((c) => (
              <li key={c.contact_id} data-testid={`contact-${c.contact_id}`}>
                <button
                  onClick={() => setSelectedId(c.contact_id)}
                  className={`w-full text-left py-3 px-2 transition-colors ${selectedId === c.contact_id ? "bg-[#0F0D0C]" : "hover:bg-[#0F0D0C]/50"}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-serif text-lg truncate">
                          {c.display_name || <span className="text-[#A39A8F] italic">Anonymous</span>}
                        </div>
                        {c.ai_paused && <BotOff className="w-3.5 h-3.5 text-[#D95B38] shrink-0" strokeWidth={1.5} />}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8F] truncate">
                        {c.status === "followup" && <span className="text-[#D95B38] mr-2">● Follow-up</span>}
                        {c.channel || "web"} · {c.message_count || 0} msg{c.message_count === 1 ? "" : "s"}
                      </div>
                    </div>
                    {c.memory && <Brain className="w-4 h-4 text-[#D95B38] shrink-0" strokeWidth={1.5} />}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="lg:col-span-8">
        {!detail ? (
          <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-10 h-full flex items-center justify-center">
            <div className="text-center font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F]">
              {contacts.length ? "Select someone to view their memory" : "Awaiting first conversation…"}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-6 md:p-8" data-testid="contact-detail">
              <div className="flex justify-between items-start gap-4 mb-6">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-2">Contact</div>
                  <h3 className="font-serif text-3xl truncate">
                    {detail.contact.display_name || <span className="italic text-[#A39A8F]">Anonymous visitor</span>}
                  </h3>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mt-2 flex flex-wrap gap-3">
                    <span>{detail.contact.channel || "web"}</span>
                    <span>·</span>
                    <span>{detail.contact.message_count} messages</span>
                    <span>·</span>
                    <span>Last seen {fmtDate(detail.contact.last_seen)}</span>
                  </div>
                </div>
                <button
                  onClick={forgetContact}
                  data-testid="forget-contact"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#D95B38] flex items-center gap-1.5 self-start"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Forget
                </button>
              </div>

              {/* Direct contact buttons */}
              <div className="flex flex-wrap gap-2 mb-6" data-testid="direct-contact-actions">
                {detail.contact.email && (
                  <a
                    href={`mailto:${detail.contact.email}`}
                    data-testid="action-email"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] hover:bg-[#F5F2EB] hover:text-[#0F0D0C] px-3 py-2 transition-colors flex items-center gap-2"
                  >
                    <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {detail.contact.email}
                  </a>
                )}
                {detail.contact.phone && (
                  <>
                    <a
                      href={`tel:${detail.contact.phone}`}
                      data-testid="action-call"
                      className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] hover:bg-[#F5F2EB] hover:text-[#0F0D0C] px-3 py-2 transition-colors flex items-center gap-2"
                    >
                      <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> Call
                    </a>
                    {waToHref(detail.contact.phone) && (
                      <a
                        href={waToHref(detail.contact.phone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="action-whatsapp"
                        className="font-mono text-[10px] uppercase tracking-[0.25em] border border-green-500/40 text-green-500 hover:bg-green-500/10 px-3 py-2 transition-colors flex items-center gap-2"
                      >
                        <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> WhatsApp
                      </a>
                    )}
                  </>
                )}
                {!detail.contact.email && !detail.contact.phone && (
                  <div className="font-mono text-xs text-[#A39A8F] italic">No direct contact info collected.</div>
                )}
              </div>

              {/* Status + AI toggle */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-2">Status</div>
                  <div className="flex gap-1 border border-[#F5F2EB]/15">
                    {[
                      { k: "open", l: "Open" },
                      { k: "followup", l: "Follow-up" },
                      { k: "resolved", l: "Resolved" },
                    ].map((s) => (
                      <button
                        key={s.k}
                        onClick={() => updateContact({ status: s.k })}
                        data-testid={`status-${s.k}`}
                        className={`flex-1 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${(detail.contact.status || "open") === s.k ? "bg-[#D95B38] text-[#F5F2EB]" : "text-[#A39A8F] hover:text-[#F5F2EB]"}`}
                      >
                        {s.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-2">AI auto-reply</div>
                  <button
                    onClick={() => updateContact({ ai_paused: !detail.contact.ai_paused })}
                    data-testid="ai-paused-toggle"
                    className={`w-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-2 transition-colors ${detail.contact.ai_paused ? "border border-[#D95B38] text-[#D95B38] hover:bg-[#D95B38] hover:text-[#F5F2EB]" : "border border-[#F5F2EB]/30 text-[#F5F2EB] hover:bg-[#F5F2EB] hover:text-[#0F0D0C]"}`}
                  >
                    {detail.contact.ai_paused ? <><BotOff className="w-3.5 h-3.5" strokeWidth={1.5} /> Paused — handle manually</> : <><Bot className="w-3.5 h-3.5" strokeWidth={1.5} /> On — agent auto-replies</>}
                  </button>
                </div>
              </div>

              <div className="border-l-2 border-[#D95B38] pl-5 mt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38]">
                    <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Memory
                  </div>
                  <button
                    onClick={refreshMemory}
                    disabled={refreshing}
                    data-testid="refresh-memory"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" strokeWidth={1.5} />}
                    {refreshing ? "Refreshing…" : "Refresh"}
                  </button>
                </div>
                {detail.contact.memory ? (
                  <div className="font-mono text-sm text-[#F5F2EB] whitespace-pre-wrap leading-relaxed" data-testid="contact-memory">
                    {detail.contact.memory}
                  </div>
                ) : (
                  <div className="font-mono text-sm text-[#A39A8F] italic">
                    No memory yet. After a few exchanges, the agent will start remembering things about this person.
                  </div>
                )}
              </div>

              {detail.contact.intake_data && Object.keys(detail.contact.intake_data).length > 0 && (
                <div className="border-l-2 border-green-500/60 pl-5 mt-6" data-testid="contact-intake">
                  <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-green-500 mb-3">
                    <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> Captured intake
                    {detail.contact.intake_complete && (
                      <span className="ml-2 px-2 py-0.5 border border-green-500/60 text-[9px]">Complete</span>
                    )}
                  </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 font-mono text-sm">
                    {Object.entries(detail.contact.intake_data).map(([k, v]) => (
                      <div key={k} data-testid={`intake-captured-${k}`} className="flex gap-2">
                        <dt className="text-[#A39A8F] uppercase tracking-[0.2em] text-[10px] mt-1">{k}</dt>
                        <dd className="text-[#F5F2EB] break-all">{String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>

            <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-6 md:p-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-4">Conversation</div>
              <div className="max-h-[400px] overflow-y-auto space-y-2 flex flex-col" data-testid="contact-messages">
                {(detail.messages || []).length === 0 && (
                  <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F]">No messages yet.</div>
                )}
                {(detail.messages || []).map((m) => (
                  <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[80%] p-3 font-mono text-sm ${
                        m.role === "user"
                          ? "bg-[#D95B38] text-[#F5F2EB] rounded-l-md rounded-tr-md"
                          : "bg-[#0F0D0C] border border-[#F5F2EB]/10 text-[#F5F2EB] rounded-r-md rounded-tl-md"
                      }`}
                    >
                      <div className="whitespace-pre-wrap">{m.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConnectTab({ agent, updateAgent, savingForm }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  return (
    <div className="space-y-8">
      <SharedSandboxPanel agent={agent} />
      {!showAdvanced ? (
        <div className="text-center py-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(true)}
            data-testid="show-byo-twilio-btn"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] hover:text-[#D95B38] transition-colors border-b border-dashed border-[#F5F2EB]/20 pb-1"
          >
            Advanced · bring your own Twilio number →
          </button>
          <p className="font-mono text-[10px] text-[#A39A8F]/60 mt-3 max-w-md mx-auto">
            For when you want your own WhatsApp Business number instead of the shared sandbox.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F]">Advanced</div>
            <button
              type="button"
              onClick={() => setShowAdvanced(false)}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#D95B38]"
            >
              Hide ↑
            </button>
          </div>
          <TwilioPanel agent={agent} onSave={updateAgent} savingForm={savingForm} />
        </div>
      )}
    </div>
  );
}

function VoiceReplyPanel({ agent }) {
  const [enabled, setEnabled] = useState(!!agent.reply_with_voice);
  const [mode, setMode] = useState(agent.voice_reply_mode || "mirror");
  const [voices, setVoices] = useState([]);
  const [clonedVoices, setClonedVoices] = useState([]);
  const [voiceId, setVoiceId] = useState(agent.premium_voice_id || "");
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [showClone, setShowClone] = useState(false);

  const reloadCloned = React.useCallback(async () => {
    try {
      const r = await api.get(`/voices/cloned`);
      setClonedVoices(r.data?.voices || []);
    } catch { /* not authed or no creds — silent */ }
  }, []);

  useEffect(() => {
    let mounted = true;
    api.get(`/voices/demo`).then((r) => {
      if (!mounted) return;
      const list = r.data?.voices || [];
      setVoices(list);
      if (!voiceId && list.length) setVoiceId(list[0].id);
    }).catch(() => {});
    reloadCloned();
    return () => { mounted = false; };
     
  }, []);

  const save = async (nextEnabled, nextVoiceId, nextMode) => {
    setBusy(true);
    try {
      await api.post(`/agents/${agent.id}/whatsapp/voice-reply`, {
        enabled: nextEnabled,
        voice_id: nextVoiceId || undefined,
        mode: nextMode || undefined,
      });
      toast.success(nextEnabled ? "Voice replies enabled" : "Voice replies disabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    await save(next, voiceId, mode);
  };

  const pickVoice = async (vid) => {
    setVoiceId(vid);
    if (enabled) await save(enabled, vid, mode);
  };

  const pickMode = async (next) => {
    setMode(next);
    if (enabled) await save(enabled, voiceId, next);
  };

  const preview = async (vid) => {
    setPreviewing(vid);
    try {
      const r = await api.get(`/voices/demo/${vid}`);
      const audio = new Audio(`data:audio/mpeg;base64,${r.data.audio_b64}`);
      await audio.play();
      audio.onended = () => setPreviewing(null);
    } catch {
      setPreviewing(null);
      toast.error("Couldn't play preview");
    }
  };

  return (
    <div className="border border-[#F5F2EB]/10 p-6" data-testid="voice-reply-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-2">Step 3 — reply with voice (optional)</div>
          <p className="font-mono text-sm text-[#E0D8CA] leading-relaxed">
            When ON, every reply is also sent as an ElevenLabs voice note. Customers hear your agent speak.
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          data-testid="voice-reply-toggle"
          aria-pressed={enabled}
          className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 disabled:opacity-60 ${
            enabled ? "bg-[#D95B38]" : "bg-[#F5F2EB]/15"
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out mt-0.5 ${
              enabled ? "translate-x-6" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      {enabled && voices.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-3">When to reply with voice</div>
          <div className="grid grid-cols-2 gap-2 mb-5" data-testid="voice-mode-toggle">
            <button
              type="button"
              onClick={() => pickMode("mirror")}
              data-testid="voice-mode-mirror"
              className={`p-3 text-left border transition-colors ${
                mode === "mirror"
                  ? "border-[#D95B38] bg-[#D95B38]/5"
                  : "border-[#F5F2EB]/10 hover:border-[#F5F2EB]/30"
              }`}
            >
              <div className="font-mono text-sm text-[#F5F2EB]">Mirror customer</div>
              <div className="font-mono text-[10px] text-[#A39A8F] mt-0.5">Voice → voice · Text → text</div>
            </button>
            <button
              type="button"
              onClick={() => pickMode("always")}
              data-testid="voice-mode-always"
              className={`p-3 text-left border transition-colors ${
                mode === "always"
                  ? "border-[#D95B38] bg-[#D95B38]/5"
                  : "border-[#F5F2EB]/10 hover:border-[#F5F2EB]/30"
              }`}
            >
              <div className="font-mono text-sm text-[#F5F2EB]">Always voice</div>
              <div className="font-mono text-[10px] text-[#A39A8F] mt-0.5">Every reply gets a voice note</div>
            </button>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-3">Choose voice</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="voice-list">
            {[...clonedVoices.map((cv) => ({
              id: cv.voice_id,
              label: `${cv.label} (cloned)`,
              description: "Your voice clone",
              _cloned_id: cv.id,
            })), ...voices].map((v) => {
              const selected = v.id === voiceId;
              return (
                <div
                  key={v.id}
                  className={`flex items-center justify-between gap-3 p-3 border transition-colors cursor-pointer ${
                    selected
                      ? "border-[#D95B38] bg-[#D95B38]/5"
                      : "border-[#F5F2EB]/10 hover:border-[#F5F2EB]/30"
                  }`}
                  onClick={() => pickVoice(v.id)}
                  data-testid={`voice-option-${v.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm text-[#F5F2EB] truncate flex items-center gap-2">
                      {v._cloned_id && <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#D95B38] bg-[#D95B38]/10 px-1.5 py-0.5">Yours</span>}
                      {v.label}
                    </div>
                    {v.description && (
                      <div className="font-mono text-[10px] text-[#A39A8F] truncate mt-0.5">{v.description}</div>
                    )}
                  </div>
                  {!v._cloned_id && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); preview(v.id); }}
                      className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8F] hover:text-[#D95B38] flex items-center gap-1 shrink-0"
                      data-testid={`voice-preview-${v.id}`}
                    >
                      {previewing === v.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                      Play
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <p className="font-mono text-[10px] text-[#A39A8F]/70 mt-3">
            Tip: voice notes use your ElevenLabs credits. Disable if you want text-only replies.
          </p>
        </div>
      )}

      {/* Voice cloning CTA — always visible, even when voice replies are off */}
      <div className="mt-5 pt-5 border-t border-[#F5F2EB]/10" data-testid="clone-voice-section">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-2">Clone your voice</div>
        <p className="font-mono text-xs text-[#A39A8F] mb-3 leading-relaxed">
          Upload 30+ seconds of clean audio and your agent will reply in your own voice.
          {!enabled && (
            <span className="block mt-1 text-[#E0D8CA]">After cloning, toggle “Reply with voice” above to start using it.</span>
          )}
        </p>
        <button
          type="button"
          onClick={() => setShowClone(true)}
          data-testid="clone-voice-cta"
          className="font-mono text-xs uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] hover:bg-[#D95B38] hover:text-[#F5F2EB] transition-colors px-5 py-3 flex items-center gap-2 btn-press"
        >
          <Mic className="w-4 h-4" strokeWidth={1.8} />
          Clone my voice
        </button>
      </div>

      {showClone && (
        <CloneVoiceModal
          onClose={() => setShowClone(false)}
          onCloned={async (newVoice) => {
            await reloadCloned();
            setVoiceId(newVoice.voice_id);
            if (enabled) await save(enabled, newVoice.voice_id, mode);
            setShowClone(false);
            toast.success("Voice cloned! Nick now speaks as you.");
          }}
        />
      )}
    </div>
  );
}

function CloneVoiceModal({ onClose, onCloned }) {
  const [credits, setCredits] = useState(null);
  const [price, setPrice] = useState(19);
  const [name, setName] = useState("My voice");
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [buyingCredit, setBuyingCredit] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get(`/voices/clone/credits`).then((r) => {
      setCredits(r.data?.credits ?? 0);
      setPrice(r.data?.price_usd ?? 19);
    }).catch(() => { setCredits(0); });
  }, []);

  const buyCredit = async () => {
    setBuyingCredit(true);
    try {
      const r = await api.post(`/voices/clone/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout");
      setBuyingCredit(false);
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose an audio file first"); return; }
    if (!consent) { toast.error("Please confirm consent"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", file);
      fd.append("name", name);
      fd.append("consent_confirmed", "true");
      const r = await api.post(`/voices/clone`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onCloned(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Voice cloning failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="clone-voice-modal"
    >
      <div
        className="bg-[#1A1715] border border-[#F5F2EB]/15 max-w-lg w-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-2">Voice clone</div>
            <h3 className="font-serif text-2xl">Use your own voice</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB]"
          >
            Close ✕
          </button>
        </div>

        {credits === null ? (
          <div className="font-mono text-sm text-[#A39A8F]">Loading…</div>
        ) : credits < 1 ? (
          <div>
            <p className="font-mono text-sm text-[#E0D8CA] mb-4 leading-relaxed">
              Voice cloning is a one-time <span className="text-[#F5F2EB]">${price}</span> purchase.
              We'll send you to Stripe, then once paid, you can come back here to upload your sample.
            </p>
            <ul className="font-mono text-xs text-[#A39A8F] space-y-1 mb-6 list-disc list-inside">
              <li>30+ seconds of clear audio (mp3/wav/m4a/ogg/webm)</li>
              <li>Speak naturally — read a paragraph or have a conversation</li>
              <li>Use the same voice you want your agent to sound like</li>
            </ul>
            <button
              type="button"
              onClick={buyCredit}
              disabled={buyingCredit}
              data-testid="buy-clone-credit"
              className="w-full font-mono text-sm uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-4 btn-press flex items-center justify-center gap-2 hover:bg-[#C44E2E] transition-colors"
            >
              {buyingCredit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" strokeWidth={1.8} />}
              Buy 1 voice clone · ${price}
            </button>
          </div>
        ) : (
          <form onSubmit={upload} className="space-y-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-green-400 border border-green-500/40 px-3 py-1.5 inline-block">
              ● {credits} credit{credits > 1 ? "s" : ""} available
            </div>
            <Field label="Voice name">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="clone-voice-name"
                className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38]"
              />
            </Field>
            <Field label="Audio sample (≥30 seconds, ≤10 MB)">
              <input
                ref={fileRef}
                type="file"
                accept="audio/mp3,audio/wav,audio/m4a,audio/ogg,audio/webm,audio/flac,.mp3,.wav,.m4a,.ogg,.webm,.flac"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="clone-voice-file"
                className="block font-mono text-xs text-[#A39A8F] file:font-mono file:text-[10px] file:uppercase file:tracking-[0.25em] file:bg-[#D95B38] file:text-[#F5F2EB] file:border-0 file:px-4 file:py-2 file:mr-3 file:cursor-pointer"
              />
              {file && (
                <div className="font-mono text-[10px] text-[#A39A8F] mt-2">
                  {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
                </div>
              )}
            </Field>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="clone-voice-consent"
                className="mt-1 accent-[#D95B38]"
              />
              <span className="font-mono text-[10px] text-[#A39A8F] leading-relaxed">
                I confirm this audio is of MY voice (or I have explicit permission from the person speaking), and I take full responsibility for any content the AI agent generates in this voice.
              </span>
            </label>
            <button
              type="submit"
              disabled={busy || !file || !consent}
              data-testid="clone-voice-submit"
              className="w-full font-mono text-sm uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-4 btn-press flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-[#C44E2E] transition-colors"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.8} />}
              {busy ? "Cloning… (10-30s)" : "Clone my voice"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function SharedSandboxPanel({ agent }) {
  const [info, setInfo] = useState(null);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [waCount, setWaCount] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get(`/agents/${agent.id}/whatsapp/sandbox/info`);
        if (!mounted) return;
        setInfo(r.data);
        if (r.data?.bound_phone) setPhone(r.data.bound_phone);
      } catch {
        if (mounted) setInfo({ configured: false });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [agent.id]);

  // Poll WhatsApp message count every 8s while bound — gives a live "it's working" pulse
  useEffect(() => {
    if (!info?.bound_phone) return undefined;
    let mounted = true;
    const tick = async () => {
      try {
        const phoneClean = info.bound_phone;
        const r = await api.get(`/agents/${agent.id}/contacts/${encodeURIComponent(`wa:${phoneClean}`)}`);
        const msgs = r.data?.messages || [];
        if (mounted) setWaCount(msgs.length);
      } catch {
        // contact doesn't exist yet (no messages received) — that's fine, count is 0
        if (mounted) setWaCount(0);
      }
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => { mounted = false; clearInterval(id); };
  }, [agent.id, info?.bound_phone]);

  const normalize = (p) => {
    let s = (p || "").trim().replace(/[\s\-\(\)]/g, "");
    if (s && !s.startsWith("+") && /^\d/.test(s)) s = "+" + s;
    return s;
  };

  const bind = async (e) => {
    e.preventDefault();
    const p = normalize(phone);
    if (!/^\+\d{8,15}$/.test(p)) {
      toast.error("Use E.164 format, e.g. +14155550123");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/agents/${agent.id}/whatsapp/sandbox/bind`, { phone: p });
      setInfo({ ...info, bound_phone: r.data.phone });
      toast.success("WhatsApp sandbox bound — try sending a message now!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to bind");
    } finally {
      setBusy(false);
    }
  };

  const unbind = async () => {
    if (!window.confirm("Unbind your phone from this agent?")) return;
    setBusy(true);
    try {
      await api.post(`/agents/${agent.id}/whatsapp/sandbox/unbind`);
      setInfo({ ...info, bound_phone: null });
      setPhone("");
      toast.success("Unbound.");
    } catch (err) {
      toast.error("Failed to unbind");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (!info?.configured) return null; // hide if shared sandbox not wired

  const bound = info.bound_phone;
  const sandboxNum = info.sandbox_number;
  const joinCode = info.join_code;

  return (
    <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10" data-testid="shared-sandbox-panel">
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3">Free · Shared sandbox</div>
          <h2 className="font-serif text-3xl mb-3">Test on WhatsApp in 60 seconds.</h2>
          <p className="font-mono text-sm text-[#A39A8F] leading-relaxed max-w-xl">
            No Twilio account needed. Bind your personal WhatsApp number below — then chat with your agent
            live on WhatsApp. Perfect for trial users.
          </p>
        </div>
        {bound && (
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-green-400 border border-green-500/40 px-3 py-1.5 whitespace-nowrap">
            ● Bound
          </div>
        )}
      </div>

      {!bound ? (
        <form onSubmit={bind} className="space-y-5 max-w-md">
          <Field label="Your WhatsApp phone (E.164)">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+14155550123"
              data-testid="sandbox-phone-input"
              className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38]"
            />
          </Field>
          <button
            type="submit"
            disabled={busy}
            data-testid="sandbox-bind-btn"
            className="w-full sm:w-auto font-mono text-sm uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-8 py-4 btn-press flex items-center justify-center gap-3 disabled:opacity-60 hover:bg-[#C44E2E] transition-colors shadow-lg shadow-[#D95B38]/20"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" strokeWidth={1.8} />}
            Connect my WhatsApp →
          </button>
        </form>
      ) : (
        <div className="space-y-6">
          <div className="border border-[#D95B38]/30 bg-[#D95B38]/5 p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3">Step 1 — join the sandbox</div>
            <p className="font-mono text-sm text-[#E0D8CA] mb-4 leading-relaxed">
              Open WhatsApp on <span className="text-[#F5F2EB]">{bound}</span> and send this message to{" "}
              <span className="text-[#F5F2EB]">{sandboxNum}</span>:
            </p>
            <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-4 font-mono text-lg text-[#F5F2EB] flex items-center justify-between gap-3">
              <span data-testid="sandbox-join-code">{joinCode}</span>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(joinCode); toast.success("Copied"); }}
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#D95B38] flex items-center gap-1.5"
              >
                <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Copy
              </button>
            </div>
            <p className="font-mono text-xs text-[#A39A8F] mt-3">
              You'll get a reply: "✅ You are all set!". Then send any message — your agent will reply.
            </p>
          </div>

          <div className="border border-[#F5F2EB]/10 p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F]">Step 2 — chat</div>
              {waCount !== null && (
                <div
                  data-testid="wa-message-count"
                  className={`font-mono text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 ${
                    waCount > 0
                      ? "text-green-400 border border-green-500/40"
                      : "text-[#A39A8F] border border-[#F5F2EB]/15"
                  }`}
                >
                  {waCount > 0 ? `● ${waCount} messages received` : "○ waiting for first message"}
                </div>
              )}
            </div>
            <p className="font-mono text-sm text-[#E0D8CA] mb-1">
              From WhatsApp number: <span className="text-[#F5F2EB]">{bound}</span>
            </p>
            <p className="font-mono text-xs text-[#A39A8F] mb-3">
              Every message you send to {sandboxNum} will be answered by <em>{agent.name}</em>.
            </p>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] flex items-center gap-2 pt-2 border-t border-[#F5F2EB]/10">
              <Mic className="w-3.5 h-3.5" strokeWidth={1.8} />
              Voice notes auto-transcribed via Whisper
            </div>
          </div>

          <VoiceReplyPanel agent={agent} />

          <button
            type="button"
            onClick={unbind}
            disabled={busy}
            data-testid="sandbox-unbind-btn"
            className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors px-6 py-3 flex items-center gap-2"
          >
            <PowerOff className="w-4 h-4" strokeWidth={1.5} /> Unbind phone
          </button>
        </div>
      )}
    </div>
  );
}

function TwilioPanel({ agent, onSave, savingForm }) {
  const [form, setForm] = useState({
    twilio_account_sid: agent.twilio_account_sid || "",
    twilio_auth_token: "",
    twilio_whatsapp_number: agent.twilio_whatsapp_number || "",
  });
  const tokenSet = agent.twilio_auth_token_set;
  const tokenLast4 = agent.twilio_auth_token_last4;

  const submit = (e) => {
    e.preventDefault();
    const patch = {
      twilio_account_sid: form.twilio_account_sid.trim() || null,
      twilio_whatsapp_number: form.twilio_whatsapp_number.trim() || null,
    };
    if (form.twilio_auth_token.trim()) {
      patch.twilio_auth_token = form.twilio_auth_token.trim();
    }
    onSave(patch);
  };

  const disconnect = () => {
    if (!window.confirm("Disconnect Twilio? Credentials will be cleared.")) return;
    onSave({
      twilio_account_sid: null,
      twilio_auth_token: null,
      twilio_whatsapp_number: null,
    });
    setForm({ twilio_account_sid: "", twilio_auth_token: "", twilio_whatsapp_number: "" });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="connect-panel">
      <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">WhatsApp via Twilio</div>
        <h2 className="font-serif text-3xl mb-4">Bring your own Twilio</h2>
        <p className="font-mono text-sm text-[#A39A8F] mb-6 leading-relaxed">
          Paste your Twilio credentials below. We store them encrypted at rest. Get them at{" "}
          <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-[#D95B38] hover:underline">
            console.twilio.com
          </a>.
        </p>

        <Link to="/help/whatsapp" data-testid="twilio-howto-link"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] px-4 py-2.5 hover:bg-[#D95B38] hover:text-[#F5F2EB] transition-colors mb-6">
          <Code className="w-3.5 h-3.5" strokeWidth={1.8} /> Step-by-step how-to →
        </Link>

        <div className="border-l-2 border-[#D95B38] pl-4 mb-6" data-testid="twilio-status">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F]">Status</div>
          <div className="font-serif text-2xl flex items-center gap-3 mt-1">
            <span className={`w-2 h-2 rounded-full ${agent.whatsapp_connected ? "bg-green-500 pulse-green" : "bg-[#A39A8F]"}`} />
            {agent.whatsapp_connected ? "Connected" : "Not connected"}
          </div>
          {agent.whatsapp_connected && agent.whatsapp_number && (
            <div className="font-mono text-sm text-[#E0D8CA] mt-2 flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-[#D95B38]" strokeWidth={1.5} />
              {agent.whatsapp_number}
            </div>
          )}
        </div>

        <form onSubmit={submit} className="space-y-5">
          <Field label="Twilio Account SID">
            <input
              value={form.twilio_account_sid}
              onChange={(e) => setForm({ ...form, twilio_account_sid: e.target.value })}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              data-testid="twilio-sid"
              className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38]"
            />
          </Field>
          <Field label={tokenSet ? `Twilio Auth Token (current ends in …${tokenLast4})` : "Twilio Auth Token"}>
            <input
              type="password"
              value={form.twilio_auth_token}
              onChange={(e) => setForm({ ...form, twilio_auth_token: e.target.value })}
              placeholder={tokenSet ? "leave blank to keep current" : "paste auth token"}
              data-testid="twilio-token"
              className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38]"
            />
          </Field>
          <Field label="WhatsApp sandbox number">
            <input
              value={form.twilio_whatsapp_number}
              onChange={(e) => setForm({ ...form, twilio_whatsapp_number: e.target.value })}
              placeholder="whatsapp:+14155238886"
              data-testid="twilio-number"
              className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 py-3 font-mono text-sm focus:outline-none focus:border-[#D95B38]"
            />
          </Field>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={savingForm}
              data-testid="twilio-save"
              className="font-mono text-xs uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] hover:bg-[#D95B38] hover:text-[#F5F2EB] transition-colors px-6 py-3 btn-press flex items-center gap-2 disabled:opacity-60"
            >
              {savingForm ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
              Save credentials
            </button>
            {agent.whatsapp_connected && (
              <button
                type="button"
                onClick={disconnect}
                data-testid="twilio-disconnect"
                className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors px-6 py-3 flex items-center gap-2"
              >
                <PowerOff className="w-4 h-4" strokeWidth={1.5} /> Disconnect
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">What this unlocks</div>
        <h3 className="font-serif text-2xl mb-6">WhatsApp, end-to-end.</h3>
        <ul className="space-y-4 font-mono text-sm">
          {[
            "Inbound voice notes → Whisper transcription → Claude → voice reply",
            "Per-contact memory keyed on phone number",
            "Intake form runs automatically on every new chat",
            "Auto-pause whitelist works the same as web",
          ].map((l) => (
            <li key={l} className="flex items-start gap-3">
              <span className="text-[#D95B38] mt-1">→</span>
              <span className="text-[#E0D8CA]">{l}</span>
            </li>
          ))}
        </ul>
        <div className="mt-8 p-4 border border-[#D95B38]/40 bg-[#D95B38]/5 font-mono text-xs text-[#E0D8CA] leading-relaxed">
          <strong className="text-[#D95B38]">Note:</strong> Credentials are stored, but the live webhook + send-message bridge is not yet activated. Once you save, ping us to flip the switch and verify your sandbox number.
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-1">{label}</span>
      {children}
    </label>
  );
}

function VoicePicker({ voices, selected, onSelect }) {
  const [playing, setPlaying] = useState(null);   // voice name currently playing
  const [loading, setLoading] = useState(null);   // voice name currently loading
  const audioRef = useRef(null);
  const cacheRef = useRef({}); // { voice: audio_b64 }

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(null);
  };

  const preview = async (voice) => {
    if (playing === voice) { stop(); return; }
    stop();
    setLoading(voice);
    try {
      let b64 = cacheRef.current[voice];
      if (!b64) {
        const res = await api.get(`/voice-preview/${voice}`);
        b64 = res.data.audio_b64;
        cacheRef.current[voice] = b64;
      }
      const audio = new Audio(`data:audio/mpeg;base64,${b64}`);
      audioRef.current = audio;
      audio.onended = () => setPlaying(null);
      audio.onerror = () => { setPlaying(null); toast.error("Could not play preview"); };
      setPlaying(voice);
      audio.play();
    } catch (e) {
      toast.error("Could not load voice preview — try again in a moment");
    } finally {
      setLoading(null);
    }
  };

  useEffect(() => () => stop(), []); // cleanup on unmount

  return (
    <div className="grid grid-cols-3 gap-2" data-testid="voice-picker">
      {voices.map((v) => {
        const isSelected = selected === v;
        const isPlaying = playing === v;
        const isLoading = loading === v;
        return (
          <div
            key={v}
            onClick={() => onSelect(v)}
            data-testid={`voice-card-${v}`}
            className={`group relative cursor-pointer border px-3 py-2.5 transition-colors ${
              isSelected
                ? "border-[#D95B38] bg-[#D95B38]/10"
                : "border-[#F5F2EB]/15 hover:border-[#F5F2EB]/40"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className={`font-mono text-xs capitalize ${isSelected ? "text-[#D95B38]" : "text-[#F5F2EB]"}`}>{v}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); preview(v); }}
                data-testid={`voice-preview-${v}`}
                aria-label={isPlaying ? `Stop ${v}` : `Preview ${v}`}
                className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors ${
                  isPlaying ? "bg-[#D95B38] text-[#F5F2EB]" : "bg-[#F5F2EB]/10 text-[#F5F2EB] hover:bg-[#F5F2EB]/20"
                }`}
              >
                {isLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-3 h-3" strokeWidth={2} />
                ) : (
                  <Play className="w-3 h-3 ml-[1px]" strokeWidth={2} />
                )}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PremiumVoiceCard({ agentId, form, setForm, onSave }) {
  const [voices, setVoices] = useState([]);
  const [addon, setAddon] = useState(null);  // { premium_voice_active, premium_voice_price_usd }
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  const [showClone, setShowClone] = useState(false);
  const [clonedVoices, setClonedVoices] = useState([]);
  const audioRef = useRef(null);

  const reloadCloned = React.useCallback(async () => {
    try {
      const r = await api.get(`/voices/cloned`);
      setClonedVoices(r.data?.voices || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { reloadCloned(); }, [reloadCloned]);

  useEffect(() => {
    Promise.all([
      api.get("/voices/elevenlabs").then((r) => setVoices(r.data.voices)).catch(() => {}),
      api.get(`/billing/agents/${agentId}/addons`).then((r) => setAddon(r.data)).catch(() => setAddon(null)),
    ]).finally(() => setLoading(false));
  }, [agentId]);

  const isActive = !!addon?.premium_voice_active;
  const price = addon?.premium_voice_price_usd ?? 4.99;

  const activate = async () => {
    setActivating(true);
    try {
      await api.post(`/billing/agents/${agentId}/addons/premium-voice`);
      const r = await api.get(`/billing/agents/${agentId}/addons`);
      setAddon(r.data);
      toast.success(`Premium voice activated — $${price}/mo added to your next invoice.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't activate. Try again.");
    } finally {
      setActivating(false);
    }
  };

  const deactivate = async () => {
    if (!window.confirm("Turn off premium voice for this agent? Replies will go back to the standard voice.")) return;
    try {
      await api.delete(`/billing/agents/${agentId}/addons/premium-voice`);
      const r = await api.get(`/billing/agents/${agentId}/addons`);
      setAddon(r.data);
      setForm({ ...form, premium_voice_enabled: false });
      toast.success("Premium voice turned off.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't deactivate.");
    }
  };

  const preview = async (voiceId) => {
    if (playing === voiceId) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    setPreviewLoading(voiceId);
    try {
      const r = await api.get(`/voice-preview/${voiceId}`);
      const a = new Audio(`data:audio/mpeg;base64,${r.data.audio_b64}`);
      audioRef.current = a;
      a.onended = () => setPlaying(null);
      setPlaying(voiceId);
      a.play();
    } catch (e) {
      toast.error("Could not load premium preview");
    } finally {
      setPreviewLoading(null);
    }
  };

  const selectAndSave = async (voiceId) => {
    setForm({ ...form, premium_voice_enabled: true, premium_voice_id: voiceId });
    try {
      await onSave({ premium_voice_enabled: true, premium_voice_id: voiceId });
    } catch {}
  };

  const disable = async () => {
    setForm({ ...form, premium_voice_enabled: false });
    try {
      await onSave({ premium_voice_enabled: false });
    } catch {}
  };

  if (loading) return null;

  return (
    <div
      data-testid="premium-voice-card"
      className="border border-[#D95B38]/30 bg-gradient-to-br from-[#D95B38]/5 to-transparent p-6"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-2 flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Premium voices · ElevenLabs
          </div>
          <h3 className="font-serif text-xl text-[#F5F2EB] leading-tight">
            Sound like a real human, <span className="italic text-[#D95B38]">not a phone tree.</span>
          </h3>
          <p className="font-mono text-xs text-[#A39A8F] mt-2 leading-relaxed max-w-md">
            ElevenLabs' studio-grade voices. Same agent, dramatically more lifelike.
            Per-agent add-on so you only pay for the agents that need it.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-serif text-3xl text-[#D95B38]">${price}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7A7068]">per agent / month</div>
        </div>
      </div>

      {!isActive ? (
        <button
          type="button"
          onClick={activate}
          disabled={activating}
          data-testid="premium-voice-activate"
          className="w-full font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] hover:bg-[#c14d2e] py-3 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {activating ? <><Loader2 className="w-3 h-3 animate-spin" /> Activating…</> : `Activate premium voice — +$${price}/mo`}
        </button>
      ) : (
        <>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-3">Pick a voice for this agent</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="premium-voice-grid">
            {voices.map((v) => {
              const isSelected = form.premium_voice_id === v.id && form.premium_voice_enabled;
              const isPlaying = playing === v.id;
              const isLoad = previewLoading === v.id;
              return (
                <div
                  key={v.id}
                  data-testid={`premium-voice-card-${v.label.toLowerCase()}`}
                  onClick={() => selectAndSave(v.id)}
                  className={`group cursor-pointer border px-3 py-3 transition-colors ${
                    isSelected ? "border-[#D95B38] bg-[#D95B38]/10" : "border-[#F5F2EB]/15 hover:border-[#F5F2EB]/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className={`font-serif text-sm ${isSelected ? "text-[#D95B38]" : "text-[#F5F2EB]"}`}>{v.label}</div>
                      <div className="font-mono text-[10px] text-[#7A7068] mt-1 leading-tight">{v.description}</div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); preview(v.id); }}
                      data-testid={`premium-voice-preview-${v.label.toLowerCase()}`}
                      aria-label={isPlaying ? `Stop ${v.label}` : `Preview ${v.label}`}
                      className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full ${
                        isPlaying ? "bg-[#D95B38] text-[#F5F2EB]" : "bg-[#F5F2EB]/10 text-[#F5F2EB] hover:bg-[#F5F2EB]/20"
                      }`}
                    >
                      {isLoad ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : isPlaying ? <Pause className="w-3.5 h-3.5" />
                        : <Play className="w-3.5 h-3.5 ml-[1px]" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-3 justify-between items-center pt-3 border-t border-[#F5F2EB]/5">
            {form.premium_voice_enabled ? (
              <button
                type="button"
                onClick={disable}
                data-testid="premium-voice-disable"
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] transition-colors"
              >
                Switch back to standard voice
              </button>
            ) : <span className="font-mono text-[10px] text-[#7A7068]">Click a voice above to enable.</span>}
            <button
              type="button"
              onClick={deactivate}
              data-testid="premium-voice-cancel-addon"
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7A7068] hover:text-red-400 transition-colors"
            >
              Cancel ${price}/mo add-on
            </button>
          </div>
        </>
      )}

      {/* Voice cloning entry point — works whether premium add-on is active or not */}
      <div className="mt-5 pt-5 border-t border-[#F5F2EB]/10" data-testid="configure-clone-voice-section">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-1 flex items-center gap-2">
              <Mic className="w-3 h-3" /> Clone your own voice
            </div>
            <p className="font-mono text-xs text-[#A39A8F] leading-relaxed max-w-md">
              Upload 30+ seconds of clean audio and your agent will reply in your own voice.
              {clonedVoices.length > 0 && (
                <span className="block mt-1 text-[#E0D8CA]">You already have {clonedVoices.length} cloned voice{clonedVoices.length > 1 ? "s" : ""}.</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowClone(true)}
            data-testid="configure-clone-voice-cta"
            className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] hover:bg-[#D95B38] hover:text-[#F5F2EB] transition-colors px-4 py-2.5 flex items-center gap-2 btn-press shrink-0"
          >
            <Mic className="w-3.5 h-3.5" strokeWidth={1.8} />
            Clone my voice
          </button>
        </div>
      </div>

      {showClone && (
        <CloneVoiceModal
          onClose={() => setShowClone(false)}
          onCloned={async (newVoice) => {
            await reloadCloned();
            setForm({ ...form, premium_voice_id: newVoice.voice_id, premium_voice_enabled: true });
            try { await onSave({ premium_voice_id: newVoice.voice_id, premium_voice_enabled: true }); } catch {}
            setShowClone(false);
            toast.success("Voice cloned! Your agent now speaks as you.");
          }}
        />
      )}
    </div>
  );
}

function MetaChannelsPanel({ agentId }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeChannel, setActiveChannel] = useState("messenger");

  const reload = async () => {
    try {
      const r = await api.get(`/agents/${agentId}/meta`);
      setCfg(r.data);
    } catch (e) {
      toast.error("Could not load Meta config");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="font-mono text-xs text-[#A39A8F]" data-testid="meta-loading">Loading…</div>;
  if (!cfg) return null;

  const channels = [
    { k: "messenger", l: "Facebook Messenger", status: cfg.messenger.status },
    { k: "instagram", l: "Instagram DMs", status: cfg.instagram.status },
    { k: "whatsapp",  l: "WhatsApp Cloud", status: cfg.whatsapp_cloud.status },
  ];

  return (
    <div className="space-y-8" data-testid="meta-channels-panel">
      <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Meta channels</div>
        <h2 className="font-serif text-3xl mb-2">Connect Messenger, Instagram, and WhatsApp Cloud.</h2>
        <p className="font-mono text-sm text-[#A39A8F] mb-6 max-w-2xl leading-relaxed">
          Three Meta-owned channels, one webhook. Paste your app credentials below, then point your Meta App's webhook at the URL we generate for this agent.
        </p>
        <Link to="/help/meta-channels" data-testid="meta-howto-link"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] px-4 py-2.5 hover:bg-[#D95B38] hover:text-[#F5F2EB] transition-colors">
          <Code className="w-3.5 h-3.5" strokeWidth={1.8} /> Step-by-step setup →
        </Link>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <SharedCredentials cfg={cfg} agentId={agentId} />
        </div>
      </div>

      {/* Channel tabs */}
      <div className="border-b border-[#F5F2EB]/10 flex gap-1 flex-wrap">
        {channels.map((c) => (
          <button key={c.k} onClick={() => setActiveChannel(c.k)} data-testid={`meta-tab-${c.k}`}
            className={`px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors flex items-center gap-2 ${activeChannel === c.k ? "bg-[#D95B38] text-[#F5F2EB]" : "text-[#A39A8F] hover:text-[#F5F2EB]"}`}>
            {c.l}
            <span className={`w-1.5 h-1.5 rounded-full ${c.status === "connected" ? "bg-[#7CFC8A]" : "bg-[#A39A8F]/40"}`} />
          </button>
        ))}
      </div>

      {activeChannel === "messenger" && (
        <MessengerForm cfg={cfg.messenger} agentId={agentId} onSaved={reload} />
      )}
      {activeChannel === "instagram" && (
        <InstagramForm cfg={cfg.instagram} agentId={agentId} onSaved={reload} />
      )}
      {activeChannel === "whatsapp" && (
        <WhatsAppCloudForm cfg={cfg.whatsapp_cloud} agentId={agentId} onSaved={reload} />
      )}
    </div>
  );
}

function SharedCredentials({ cfg, agentId }) {
  const copy = async (txt, label) => {
    try { await navigator.clipboard.writeText(txt); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <>
      <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2">Webhook URL (paste in Meta App → Webhooks)</div>
        <div className="font-mono text-xs text-[#E0D8CA] break-all mb-3" data-testid="meta-webhook-url">{cfg.webhook_url}</div>
        <button onClick={() => copy(cfg.webhook_url, "URL")} data-testid="meta-copy-webhook"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] hover:underline">Copy →</button>
      </div>
      <div className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2">Verify token (paste in Meta App → Webhooks)</div>
        <div className="font-mono text-xs text-[#E0D8CA] break-all mb-3" data-testid="meta-verify-token">{cfg.verify_token}</div>
        <button onClick={() => copy(cfg.verify_token, "Token")} data-testid="meta-copy-token"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] hover:underline">Copy →</button>
      </div>
    </>
  );
}

function MetaSubmitButton({ loading, children }) {
  return (
    <button type="submit" disabled={loading} data-testid="meta-submit"
      className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-3 btn-press disabled:opacity-60 inline-flex items-center gap-2">
      {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {children}
    </button>
  );
}

function MessengerForm({ cfg, agentId, onSaved }) {
  const [appSecret, setAppSecret] = useState("");
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/agents/${agentId}/meta/messenger`, { app_secret: appSecret, page_access_token: token });
      toast.success("Messenger connected");
      setAppSecret(""); setToken("");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Messenger? Your agent will stop replying to Page messages.")) return;
    try { await api.delete(`/agents/${agentId}/meta/messenger`); onSaved(); toast.success("Disconnected"); }
    catch { toast.error("Could not disconnect"); }
  };
  return (
    <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8" data-testid="meta-messenger-form">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-1">Channel · Facebook Messenger</div>
          <h3 className="font-serif text-2xl">{cfg.page_id ? cfg.page_id : "Connect your Facebook Page"}</h3>
        </div>
        {cfg.status === "connected" && (
          <button onClick={disconnect} data-testid="messenger-disconnect"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] hover:underline">Disconnect →</button>
        )}
      </div>
      <form onSubmit={submit} className="space-y-5">
        <Field label="App Secret">
          <input type="password" required value={appSecret} onChange={(e) => setAppSecret(e.target.value)}
            placeholder={cfg.app_secret_set ? "•••••• (already set — paste again to update)" : "Paste your App Secret"}
            data-testid="messenger-app-secret"
            className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]" />
        </Field>
        <Field label="Page Access Token">
          <input type="password" required value={token} onChange={(e) => setToken(e.target.value)}
            placeholder={cfg.page_access_token_preview ? `••••${cfg.page_access_token_preview} (paste again to update)` : "EAAB... (from Meta App → Messenger → Settings)"}
            data-testid="messenger-page-token"
            className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]" />
        </Field>
        <MetaSubmitButton loading={saving}>{saving ? "Saving…" : "Save & connect →"}</MetaSubmitButton>
      </form>
    </div>
  );
}

function InstagramForm({ cfg, agentId, onSaved }) {
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/agents/${agentId}/meta/instagram`, { page_access_token: token });
      toast.success("Instagram connected");
      setToken("");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Instagram?")) return;
    try { await api.delete(`/agents/${agentId}/meta/instagram`); onSaved(); toast.success("Disconnected"); } catch {}
  };
  return (
    <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8" data-testid="meta-instagram-form">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-1">Channel · Instagram DMs</div>
          <h3 className="font-serif text-2xl">Reply to Instagram DMs automatically</h3>
        </div>
        {cfg.status === "connected" && (
          <button onClick={disconnect} data-testid="instagram-disconnect"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] hover:underline">Disconnect →</button>
        )}
      </div>
      <p className="font-mono text-xs text-[#A39A8F] mb-5">Uses the same App Secret you saved for Messenger above. Just paste the IG-linked Page Access Token.</p>
      <form onSubmit={submit} className="space-y-5">
        <Field label="Page Access Token (Instagram)">
          <input type="password" required value={token} onChange={(e) => setToken(e.target.value)}
            placeholder={cfg.page_access_token_preview ? `••••${cfg.page_access_token_preview} (paste again to update)` : "EAAB..."}
            data-testid="instagram-token"
            className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]" />
        </Field>
        <MetaSubmitButton loading={saving}>{saving ? "Saving…" : "Save & connect →"}</MetaSubmitButton>
      </form>
    </div>
  );
}

function WhatsAppCloudForm({ cfg, agentId, onSaved }) {
  const [phoneId, setPhoneId] = useState("");
  const [token, setToken] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { phone_number_id: phoneId, access_token: token };
      if (appSecret) body.app_secret = appSecret;
      await api.post(`/agents/${agentId}/meta/whatsapp`, body);
      toast.success("WhatsApp Cloud connected");
      setPhoneId(""); setToken(""); setAppSecret("");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect WhatsApp Cloud?")) return;
    try { await api.delete(`/agents/${agentId}/meta/whatsapp`); onSaved(); toast.success("Disconnected"); } catch {}
  };
  return (
    <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8" data-testid="meta-whatsapp-form">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-1">Channel · WhatsApp Cloud (Meta direct)</div>
          <h3 className="font-serif text-2xl">{cfg.phone_number_id ? `Phone ID ${cfg.phone_number_id}` : "Skip Twilio — go direct via Meta"}</h3>
        </div>
        {cfg.status === "connected" && (
          <button onClick={disconnect} data-testid="whatsapp-cloud-disconnect"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] hover:underline">Disconnect →</button>
        )}
      </div>
      <p className="font-mono text-xs text-[#A39A8F] mb-5">Alternative to Twilio — connects directly to Meta's WhatsApp Cloud API. Cheaper at scale, but Twilio is faster to set up.</p>
      <form onSubmit={submit} className="space-y-5">
        <Field label="Phone Number ID">
          <input required value={phoneId} onChange={(e) => setPhoneId(e.target.value)}
            placeholder="e.g. 123456789012345 (from Meta App → WhatsApp → API Setup)"
            data-testid="whatsapp-phone-id"
            className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]" />
        </Field>
        <Field label="Permanent Access Token">
          <input type="password" required value={token} onChange={(e) => setToken(e.target.value)}
            placeholder={cfg.access_token_preview ? `••••${cfg.access_token_preview} (paste again to update)` : "EAAB..."}
            data-testid="whatsapp-access-token"
            className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]" />
        </Field>
        <Field label="App Secret (optional — only if different from Messenger above)">
          <input type="password" value={appSecret} onChange={(e) => setAppSecret(e.target.value)}
            placeholder="Leave blank to reuse"
            data-testid="whatsapp-app-secret"
            className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]" />
        </Field>
        <MetaSubmitButton loading={saving}>{saving ? "Saving…" : "Save & connect →"}</MetaSubmitButton>
      </form>
    </div>
  );
}


